import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faRotateRight, faPen, faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { fetchCreatives } from "../../services/creativeLibraryService";

// Type de bibliothèque correspondant à chaque capacité Studio (filtre l'historique).
const TYPE_FOR_CAPABILITY = {
  text2image: "image", tryon: "image",
  text2video: "video", image2video: "video", editvideo: "video", avatar: "video", ugc: "video",
  voiceover: "audio",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  if (diffMin < 24 * 60) return `il y a ${Math.round(diffMin / 60)} h`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
};

// Historique des générations récentes — vignette, date, modèle, frames de
// guidage utilisées (le cas échéant) et actions Aperçu / Recréer / Éditer.
const HistoryPanel = ({ capability, refreshKey, onRecreate, onEdit }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const type = TYPE_FOR_CAPABILITY[capability] || "image";
    // scope "all" : les créations de l'utilisateur ET l'Ads Library publique
    // OneBotAds. Sans ça le panneau est vide pour tout compte neuf — alors que
    // la bibliothèque publique contient déjà de quoi démarrer.
    fetchCreatives({ type, pageSize: 6, scope: "all" })
      .then((res) => { if (!cancelled) setItems(res?.creatives || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [capability, refreshKey]);

  return (
    <div className="w-full rounded-2xl bg-white border border-gray-100 shadow-sm p-3 mt-3">
      <div className="flex items-center gap-2 px-1 pb-2">
        <FontAwesomeIcon icon={faClockRotateLeft} className="text-gray-400 text-xs" />
        <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Historique</p>
      </div>

      {loading && <p className="text-xs text-gray-400 px-1 py-2">Chargement…</p>}
      {!loading && items.length === 0 && <p className="text-xs text-gray-400 px-1 py-2">Aucune création pour l'instant.</p>}

      <div className="flex flex-col gap-2">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border border-gray-100 hover:border-gray-200 p-2 transition">
            <div className="flex gap-2">
              <a href={it.url} target="_blank" rel="noreferrer" className="shrink-0">
                {it.type === "image" ? (
                  <img src={it.url} alt="" className="w-11 h-11 rounded-lg object-cover border border-gray-200" />
                ) : it.type === "audio" ? (
                  <div className="w-11 h-11 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-400 text-lg">🎙️</div>
                ) : (
                  <div className="w-11 h-11 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-lg">▶</div>
                )}
              </a>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-gray-700 truncate">
                  {it.model_id || "—"}
                  {/* Distingue une créa de l'Ads Library publique d'une des siennes */}
                  {it.is_public && (
                    <span className="ml-1 align-middle text-[8.5px] font-bold text-fuchsia-600 bg-fuchsia-50 border border-fuchsia-200 rounded-full px-1.5 py-px">
                      Library
                    </span>
                  )}
                </p>
                <p className="text-[10px] text-gray-400">{fmtDate(it.created_at)}</p>
                {(it.start_frame_url || it.end_frame_url) && (
                  <div className="flex gap-1 mt-1">
                    {it.start_frame_url && <img src={it.start_frame_url} alt="début" title="Frame de début" className="w-4 h-4 rounded object-cover border border-gray-200" />}
                    {it.end_frame_url && <img src={it.end_frame_url} alt="fin" title="Frame de fin" className="w-4 h-4 rounded object-cover border border-gray-200" />}
                  </div>
                )}
              </div>
            </div>
            {it.prompt && <p className="text-[10px] text-gray-400 mt-1.5 line-clamp-2">{it.prompt}</p>}
            <div className="flex items-center gap-1 mt-1.5">
              <a href={it.url} target="_blank" rel="noreferrer"
                className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold text-gray-500 hover:bg-gray-50 rounded-lg py-1" title="Aperçu">
                <FontAwesomeIcon icon={faEye} className="text-[10px]" /> Aperçu
              </a>
              <button onClick={() => onRecreate?.(it)}
                className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold text-indigo-600 hover:bg-indigo-50 rounded-lg py-1" title="Recréer à l'identique">
                <FontAwesomeIcon icon={faRotateRight} className="text-[10px]" /> Recréer
              </button>
              <button onClick={() => onEdit?.(it)}
                className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold text-fuchsia-600 hover:bg-fuchsia-50 rounded-lg py-1" title="Charger dans le composeur">
                <FontAwesomeIcon icon={faPen} className="text-[10px]" /> Éditer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HistoryPanel;
